/**
 * Cuarentena: payloads que no pasaron la validacion del borde.
 *
 * Un payload invalido nunca se descarta ni se "parsea a mano": se persiste con
 * los issues de Zod que lo rechazaron y queda visible aca. Esta pantalla existe
 * para responder dos cosas de una mirada: que campo fallo y con que valor llego.
 */
import { Fragment, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { IncidentFilters, PageState, QuarantineRow } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { useCan } from '../auth/auth-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { JsonPanel } from '../components/JsonPanel';
import { Pagination } from '../components/Pagination';
import { QuarantineIssues } from '../components/QuarantineIssues';
import { ReasonDialog } from '../components/ReasonDialog';
import { SelectFilter } from '../components/SelectFilter';
import type { Option } from '../lib/catalogs';
import { SYSTEM_OPTIONS, quarantineReasonLabel, systemLabel } from '../lib/catalogs';
import { useAsyncResource } from '../hooks/use-async-resource';
import { DEFAULT_PAGE_SIZE, EMPTY_INCIDENT_FILTERS } from '../lib/filters';
import { formatDateTime, shortId } from '../lib/format';
import { normalizeZodIssues } from '../lib/quarantine-issues';

const RESOLUTION_OPTIONS: readonly Option<'pending' | 'resolved'>[] = [
  { value: 'pending', label: 'Sin revisar' },
  { value: 'resolved', label: 'Revisadas' },
];

/** Primer issue, para poder escanear la tabla sin abrir cada fila. */
function firstIssueSummary(zodIssues: unknown): string {
  const issues = normalizeZodIssues(zodIssues);
  const first = issues[0];
  if (first === undefined) {
    return 'Sin issues interpretables';
  }
  const rest = issues.length > 1 ? ` (+${issues.length - 1})` : '';
  return `${first.path}: ${first.message}${rest}`;
}

export function QuarantinePage(): ReactElement {
  const api = useMonitorApi();
  const canResolve = useCan('quarantine:resolve');

  const [filters, setFilters] = useState<IncidentFilters>({
    ...EMPTY_INCIDENT_FILTERS,
    resolution: 'pending',
  });
  const [page, setPage] = useState<PageState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resolving, setResolving] = useState<QuarantineRow | null>(null);

  const queryKey = useMemo(() => JSON.stringify({ filters, page }), [filters, page]);
  const resource = useAsyncResource(() => api.listQuarantine(filters, page), [api, queryKey]);

  const rows = resource.data?.items ?? [];

  const resolve = (row: QuarantineRow, note: string): void => {
    setBusyId(row.quarantine.id);
    setActionError(null);
    setNotice(null);
    void api.resolveQuarantine(row.quarantine.id, note).then(
      () => {
        setBusyId(null);
        setResolving(null);
        setNotice(`Se marcó como revisado el registro ${shortId(row.quarantine.id)}.`);
        resource.reload();
      },
      (error: unknown) => {
        setBusyId(null);
        setActionError(error);
      },
    );
  };

  const changeFilters = (next: IncidentFilters): void => {
    setFilters(next);
    setPage((previous) => ({ ...previous, page: 1 }));
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Cuarentena</h1>
          <p className="page__subtitle">
            Payloads rechazados por la validación de contrato. Se guardan enteros, con los issues de
            Zod que los rechazaron: son la evidencia para corregir el schema o hablar con el
            proveedor.
          </p>
        </div>
        <button type="button" className="button" onClick={resource.reload}>
          Actualizar
        </button>
      </header>

      <section className="panel">
        <div className="panel__body">
          <div className="filters">
            <SelectFilter
              label="Sistema"
              value={filters.system}
              options={SYSTEM_OPTIONS}
              onChange={(value) => changeFilters({ ...filters, system: value })}
            />
            <SelectFilter
              label="Estado"
              value={filters.resolution}
              options={RESOLUTION_OPTIONS}
              allLabel="Todos"
              onChange={(value) => changeFilters({ ...filters, resolution: value })}
            />
          </div>
        </div>
      </section>

      {notice === null ? null : (
        <div className="banner banner--info" role="status">
          {notice}
        </div>
      )}
      <ErrorBanner error={actionError} />
      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>Payloads rechazados</h2>
        </header>

        {resource.loading && resource.data === null ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No hay payloads en cuarentena con estos filtros."
            hint="Todo lo que llegó calzó con su contrato."
          />
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th scope="col">Registrado</th>
                  <th scope="col">Sistema</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Primer issue</th>
                  <th scope="col">Mensaje</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const { quarantine, message } = row;
                  const expanded = expandedId === quarantine.id;
                  const resolved = quarantine.resolvedAt !== null;
                  const summary = firstIssueSummary(quarantine.zodIssues);
                  return (
                    <Fragment key={quarantine.id}>
                      <tr>
                        <td className="nowrap" data-label="Registrado">
                          {formatDateTime(quarantine.createdAt)}
                        </td>
                        <td className="nowrap" data-label="Sistema">
                          {systemLabel(quarantine.system)}
                        </td>
                        <td className="nowrap" data-label="Motivo">
                          {quarantineReasonLabel(quarantine.reason)}
                        </td>
                        <td className="table__error table__lead">
                          <span className="truncate" title={summary}>
                            {summary}
                          </span>
                        </td>
                        <td data-label="Mensaje">
                          {quarantine.messageId === null || message === null ? (
                            <span className="muted">Sin mensaje</span>
                          ) : (
                            <Link className="mono" to={`/mensajes/${quarantine.messageId}`}>
                              {shortId(quarantine.messageId)}
                            </Link>
                          )}
                        </td>
                        <td className="nowrap table__pill">
                          {resolved ? (
                            <span className="badge badge--processed">Revisado</span>
                          ) : (
                            <span className="badge badge--quarantined">Sin revisar</span>
                          )}
                        </td>
                        <td>
                          <div className="table__actions">
                            <button
                              type="button"
                              className="button button--sm button--ghost"
                              onClick={() => setExpandedId(expanded ? null : quarantine.id)}
                            >
                              {expanded ? 'Ocultar' : 'Ver issues'}
                            </button>
                            {canResolve && !resolved ? (
                              <button
                                type="button"
                                className="button button--sm"
                                disabled={busyId === quarantine.id}
                                onClick={() => {
                                  setActionError(null);
                                  setResolving(row);
                                }}
                              >
                                Marcar revisado
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {expanded ? (
                        <tr>
                          <td colSpan={7}>
                            <div className="stack">
                              <QuarantineIssues zodIssues={quarantine.zodIssues} />
                              <JsonPanel
                                title="Payload rechazado"
                                value={quarantine.payloadRaw}
                                emptyLabel="Sin payload registrado."
                              />
                              {quarantine.resolvedBy === null ? null : (
                                <p className="muted">
                                  Revisado por {quarantine.resolvedBy} el{' '}
                                  {formatDateTime(quarantine.resolvedAt)}.
                                </p>
                              )}
                              {quarantine.resolvedAt === null ? null : (
                                <p className="muted">
                                  <strong>Qué se hizo:</strong>{' '}
                                  {quarantine.resolutionNote ?? 'sin registrar (cierre anterior)'}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page.page}
          pageSize={page.pageSize}
          total={resource.data?.total ?? 0}
          onPageChange={(nextPage) => setPage((previous) => ({ ...previous, page: nextPage }))}
          onPageSizeChange={(pageSize) => setPage({ page: 1, pageSize })}
        />
      </section>

      {resolving === null ? null : (
        <ReasonDialog
          title="Marcar el payload como revisado"
          description="No reprocesa nada: cierra el registro dejando escrito qué se hizo con el payload."
          fieldLabel="Nota de revisión"
          confirmLabel="Marcar revisado"
          busy={busyId === resolving.quarantine.id}
          error={actionError}
          onCancel={() => setResolving(null)}
          onConfirm={(note) => resolve(resolving, note)}
        />
      )}
    </div>
  );
}
